import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import useIsMobile from '../../utils/useIsMobile';

const STATUS_OPTIONS = ['planning', 'active', 'on_hold', 'completed', 'cancelled'];
const TYPE_OPTIONS = ['Implementation', 'POC'];

const STATUS_STYLE = {
  planning:  { bg: '#ede9fe', color: '#7c3aed' },
  active:    { bg: '#d1fae5', color: '#065f46' },
  on_hold:   { bg: '#fef3c7', color: '#b45309' },
  completed: { bg: '#dcfce7', color: '#166534' },
  cancelled: { bg: '#f1f5f9', color: '#64748b' },
};

const TYPE_STYLE = {
  Implementation: { bg: '#dbeafe', color: '#1e40af' },
  POC:            { bg: '#fef9c3', color: '#854d0e' },
};

function Badge({ label, style }) {
  return <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 99, textTransform: 'capitalize', ...style }}>{label}</span>;
}

function ProgressBar({ done, total }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 6, background: 'var(--n-100)', borderRadius: 99, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: '#0B6623', borderRadius: 99, transition: 'width .3s' }} />
      </div>
      <span style={{ fontSize: 11, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{done}/{total}</span>
    </div>
  );
}

// ── Customer picker with search + inline prospect creation ────────────────────
function CustomerPicker({ value, onChange, products }) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [prospect, setProspect] = useState({ name: '', address: '', city: '', state: '', spoc_name: '', contact_number: '', customer_email: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const ref = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handle(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  // Search customers (active + prospects)
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ include_prospects: 'true', limit: 20 });
        if (search) params.set('search', search);
        const data = await apiFetch(`/customers?${params}`);
        setResults(data.customers || []);
      } catch (_) {}
    }, 200);
    return () => clearTimeout(t);
  }, [search, open]);

  function pick(c) {
    setSelected(c);
    onChange(c.id);
    setOpen(false);
    setSearch('');
  }

  async function createProspect(e) {
    if (e) e.preventDefault();
    if (!prospect.name.trim()) return setErr('Company name is required');
    setSaving(true); setErr('');
    try {
      const c = await apiFetch('/customers/prospect', { method: 'POST', body: prospect });
      pick(c);
      setShowAdd(false);
      setProspect({ name: '', address: '', city: '', state: '', spoc_name: '', contact_number: '', customer_email: '', notes: '' });
    } catch (e) { setErr(e.message); }
    setSaving(false);
  }

  const lbl = { fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 };
  const inp = { width: '100%', padding: '7px 10px', borderRadius: 7, border: '1.5px solid var(--n-300)', fontSize: 13, background: 'var(--bg-surface)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {/* Trigger */}
      <div onClick={() => { setOpen(o => !o); }}
        style={{ padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--n-300)', fontSize: 13,
                 background: 'var(--bg-surface)', color: selected ? 'var(--text-primary)' : 'var(--text-secondary)',
                 cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>
          {selected
            ? <>{selected.name}{selected.stage === 'prospect' && <span style={{ marginLeft: 6, fontSize: 10, background: '#fef3c7', color: '#b45309', padding: '1px 6px', borderRadius: 99, fontWeight: 700 }}>PROSPECT</span>}</>
            : 'Search or add customer…'}
        </span>
        <span style={{ fontSize: 10, color: 'var(--text-secondary)' }}>▾</span>
      </div>

      {/* Dropdown */}
      {open && !showAdd && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 200,
                      background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)',
                      borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,.12)', overflow: 'hidden' }}>
          <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--border-subtle)' }}>
            <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Type to search…"
              style={{ ...inp, border: 'none', outline: 'none', padding: '4px 0', background: 'transparent' }} />
          </div>
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            {results.length === 0 && (
              <div style={{ padding: '10px 14px', fontSize: 13, color: 'var(--text-secondary)' }}>No matches found</div>
            )}
            {results.map(c => (
              <div key={c.id} onClick={() => pick(c)}
                style={{ padding: '9px 14px', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8,
                         borderBottom: '1px solid var(--border-subtle)' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--n-50)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{c.name}</div>
                  {c.city && <div style={{ fontSize: 11, color: '#94a3b8' }}>{c.city}</div>}
                </div>
                {c.stage === 'prospect'
                  ? <span style={{ fontSize: 10, background: '#fef3c7', color: '#b45309', padding: '1px 6px', borderRadius: 99, fontWeight: 700 }}>PROSPECT</span>
                  : <span style={{ fontSize: 10, background: '#d1fae5', color: '#065f46', padding: '1px 6px', borderRadius: 99, fontWeight: 700 }}>ACTIVE</span>}
              </div>
            ))}
          </div>
          <div style={{ padding: '8px 10px', borderTop: '1px solid var(--border-subtle)' }}>
            <button type="button" onClick={() => { setShowAdd(true); setOpen(false); }}
              style={{ width: '100%', padding: '8px 0', borderRadius: 7, border: '1.5px dashed #0B6623',
                       background: '#f0fdf4', color: '#0B6623', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
              + Add New Prospect
            </button>
          </div>
        </div>
      )}

      {/* Inline prospect form */}
      {showAdd && (
        <div style={{ marginTop: 8, padding: 16, background: '#f0fdf4', border: '1.5px solid #bbf7d0',
                      borderRadius: 10 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#065f46', marginBottom: 12 }}>New Prospect Details</div>
          {err && <div style={{ fontSize: 12, color: '#dc2626', marginBottom: 8 }}>{err}</div>}
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={lbl}>Company Name *</label>
                <input style={inp} required value={prospect.name} onChange={e => setProspect(p => ({ ...p, name: e.target.value }))} />
              </div>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={lbl}>Address</label>
                <input style={inp} value={prospect.address} onChange={e => setProspect(p => ({ ...p, address: e.target.value }))} />
              </div>
              <div>
                <label style={lbl}>City</label>
                <input style={inp} value={prospect.city} onChange={e => setProspect(p => ({ ...p, city: e.target.value }))} />
              </div>
              <div>
                <label style={lbl}>State</label>
                <input style={inp} value={prospect.state} onChange={e => setProspect(p => ({ ...p, state: e.target.value }))} />
              </div>
              <div>
                <label style={lbl}>SPOC Name</label>
                <input style={inp} value={prospect.spoc_name} onChange={e => setProspect(p => ({ ...p, spoc_name: e.target.value }))} />
              </div>
              <div>
                <label style={lbl}>Mobile</label>
                <input style={inp} value={prospect.contact_number} onChange={e => setProspect(p => ({ ...p, contact_number: e.target.value }))} />
              </div>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={lbl}>SPOC Email</label>
                <input type="email" style={inp} value={prospect.customer_email} onChange={e => setProspect(p => ({ ...p, customer_email: e.target.value }))} />
              </div>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={lbl}>Notes</label>
                <textarea style={{ ...inp, resize: 'none' }} rows={2} value={prospect.notes} onChange={e => setProspect(p => ({ ...p, notes: e.target.value }))} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => { setShowAdd(false); setOpen(true); }}
                style={{ padding: '6px 14px', borderRadius: 7, border: '1.5px solid var(--n-300)', background: 'var(--bg-surface)', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: 'var(--text-secondary)' }}>
                Back to Search
              </button>
              <button type="button" disabled={saving} onClick={createProspect}
                style={{ padding: '6px 14px', borderRadius: 7, border: 'none', background: '#0B6623', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                {saving ? 'Saving…' : 'Save Prospect'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────
export default function Projects() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [projects, setProjects] = useState([]);
  const [products, setProducts] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ search: '', status: '', type: '' });
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [templates, setTemplates] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState({ title: '', type: 'Implementation', customer_id: '', product_id: '', manager_id: '', start_date: '', target_date: '', notes: '', template_id: '' });
  const [tplPreview, setTplPreview] = useState(null); // milestones with computed dates
  const [creating, setCreating] = useState(false);
  const [msg, setMsg] = useState(null);
  const searchRef = useRef(null);
  const [selIdx, setSelIdx] = useState(-1);

  function notify(type, text) { setMsg({ type, text }); setTimeout(() => setMsg(null), 5000); }

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(filters.search), 350);
    return () => clearTimeout(t);
  }, [filters.search]);

  useEffect(() => { setSelIdx(-1); setPage(1); }, [filters.status, filters.type, debouncedSearch]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (filters.status) params.set('status', filters.status);
      if (filters.type)   params.set('type', filters.type);
      if (debouncedSearch) params.set('search', debouncedSearch);
      const data = await apiFetch(`/projects?${params}`);
      setProjects(data.projects || []);
      setHasMore(data.hasMore);
    } catch (e) { notify('error', e.message); }
    setLoading(false);
  }, [page, filters.status, filters.type, debouncedSearch]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const [prod, usr, tmpl] = await Promise.all([apiFetch('/products'), apiFetch('/users/agents'), apiFetch('/project-templates')]);
        setProducts(prod.products || prod);
        setAgents(Array.isArray(usr) ? usr : []);
        setTemplates(Array.isArray(tmpl) ? tmpl : []);
      } catch (_) {}
    };
    fetchMeta();
  }, []);

  useEffect(() => {
    function onKey(e) {
      if (document.activeElement?.tagName === 'INPUT' && document.activeElement !== searchRef.current) return;
      if (document.activeElement?.tagName === 'TEXTAREA') return;
      if (e.key === 'ArrowDown') { e.preventDefault(); setSelIdx(i => Math.min(i + 1, projects.length - 1)); }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setSelIdx(i => Math.max(i - 1, -1)); }
      if (e.key === 'Enter' && selIdx >= 0) { navigate(`/admin/projects/${projects[selIdx].id}`); }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [projects, selIdx, navigate]);

  function resetNewForm() {
    setNewForm({ title: '', type: 'Implementation', customer_id: '', product_id: '', manager_id: '', start_date: '', target_date: '', notes: '', template_id: '' });
    setTplPreview(null);
  }

  // Compute milestone due dates from template + start_date for preview
  function computePreview(templateId, startDate) {
    if (!templateId) { setTplPreview(null); return; }
    const tpl = templates.find(t => t.id === templateId);
    if (!tpl) { setTplPreview(null); return; }
    // We only have milestone counts in the list; fetch full template for preview
    apiFetch(`/project-templates/${templateId}`).then(full => {
      const base = startDate ? new Date(startDate) : new Date();
      let running = new Date(base);
      const ms = (full.milestones || []).map(m => {
        const due = m.default_days ? new Date(running.getTime() + m.default_days * 86400000) : null;
        if (m.default_days) running = new Date(running.getTime() + m.default_days * 86400000);
        return { ...m, computed_due: due ? due.toISOString().slice(0, 10) : null };
      });
      setTplPreview({ name: full.name, milestones: ms });
    }).catch(() => setTplPreview(null));
  }

  function handleTplChange(templateId) {
    setNewForm(f => ({ ...f, template_id: templateId }));
    computePreview(templateId, newForm.start_date);
  }

  function handleStartDateChange(date) {
    setNewForm(f => ({ ...f, start_date: date }));
    if (newForm.template_id) computePreview(newForm.template_id, date);
  }

  async function handleCreate(e) {
    e.preventDefault();
    if (!newForm.customer_id) return notify('error', 'Please select or add a customer');
    setCreating(true);
    try {
      const proj = await apiFetch('/projects', { method: 'POST', body: { ...newForm, manager_id: newForm.manager_id || user.id } });
      notify('success', `Project ${proj.project_number} created`);
      setShowNew(false);
      resetNewForm();
      navigate(`/admin/projects/${proj.id}`);
    } catch (e) { notify('error', e.message); }
    setCreating(false);
  }

  const card = { background: 'var(--bg-surface)', borderRadius: 12, border: '1px solid var(--border-subtle)' };
  const inputStyle = { padding: '7px 12px', borderRadius: 8, border: '1.5px solid var(--n-300)', fontSize: 13, background: 'var(--bg-surface)', color: 'var(--text-primary)', outline: 'none' };
  const lbl = { fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Projects</div>
          <div className="page-sub">Implementation & POC engagements</div>
        </div>
        <button className="btn btn-primary" onClick={() => { resetNewForm(); setShowNew(true); }}>+ New Project</button>
      </div>

      {msg && (
        <div style={{ marginBottom: 16, padding: '10px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
          background: msg.type === 'success' ? '#d1fae5' : '#fee2e2',
          border: `1px solid ${msg.type === 'success' ? '#6ee7b7' : '#fca5a5'}`,
          color: msg.type === 'success' ? '#065f46' : '#dc2626' }}>
          {msg.text}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input ref={searchRef} className="input" style={{ maxWidth: 280 }} placeholder="Search projects…"
          value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} />
        <select style={inputStyle} value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
          <option value="">All Status</option>
          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </select>
        <select style={inputStyle} value={filters.type} onChange={e => setFilters(f => ({ ...f, type: e.target.value }))}>
          <option value="">All Types</option>
          {TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      {/* Table */}
      <div style={{ ...card, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>Loading…</div>
        ) : projects.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 48, color: 'var(--text-secondary)' }}>
            No projects found. Create one to get started.
          </div>
        ) : isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {projects.map(p => {
              const ss = STATUS_STYLE[p.status] || STATUS_STYLE.planning;
              const ts = TYPE_STYLE[p.type] || TYPE_STYLE.Implementation;
              const pct = p.task_count > 0 ? Math.round((p.tasks_done / p.task_count) * 100) : 0;
              return (
                <div key={p.id} onClick={() => navigate(`/admin/projects/${p.id}`)}
                  style={{ padding: '12px 14px', borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{p.title}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, fontFamily: 'monospace' }}>{p.project_number}</div>
                    </div>
                    <Badge label={p.type} style={ts} />
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 6 }}>
                    {p.customer_name}
                    {p.customer_stage === 'prospect' && <span style={{ marginLeft: 6, fontSize: 10, background: '#fef3c7', color: '#b45309', padding: '1px 6px', borderRadius: 99, fontWeight: 700 }}>PROSPECT</span>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
                    <Badge label={p.status.replace('_', ' ')} style={ss} />
                    <div style={{ flex: 1, minWidth: 90 }}><ProgressBar done={p.tasks_done} total={p.task_count} /></div>
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                      {p.manager_name || '—'}{p.target_date ? ` · ${new Date(p.target_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}` : ''}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--n-50)' }}>
                {['Project', 'Customer', 'Type', 'Status', 'Progress', 'Manager', 'Target Date'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)',
                                        textAlign: 'left', textTransform: 'uppercase', letterSpacing: .5 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {projects.map((p, idx) => {
                const ss = STATUS_STYLE[p.status] || STATUS_STYLE.planning;
                const ts = TYPE_STYLE[p.type] || TYPE_STYLE.Implementation;
                const isActive = selIdx === idx;
                return (
                  <tr key={p.id} onClick={() => navigate(`/admin/projects/${p.id}`)}
                    style={{ borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer',
                             background: isActive ? 'var(--action-bg)' : 'transparent',
                             outline: isActive ? '2px solid #0B6623' : 'none', outlineOffset: -2 }}>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{p.title}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, fontFamily: 'monospace' }}>{p.project_number}</div>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{p.customer_name}</div>
                      {p.customer_stage === 'prospect' && (
                        <span style={{ fontSize: 10, background: '#fef3c7', color: '#b45309', padding: '1px 6px', borderRadius: 99, fontWeight: 700 }}>PROSPECT</span>
                      )}
                    </td>
                    <td style={{ padding: '12px 14px' }}><Badge label={p.type} style={ts} /></td>
                    <td style={{ padding: '12px 14px' }}><Badge label={p.status.replace('_', ' ')} style={ss} /></td>
                    <td style={{ padding: '12px 14px', minWidth: 120 }}>
                      <ProgressBar done={p.tasks_done} total={p.task_count} />
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: 13 }}>{p.manager_name || '—'}</td>
                    <td style={{ padding: '12px 14px', fontSize: 13, color: 'var(--text-secondary)' }}>
                      {p.target_date ? new Date(p.target_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {(page > 1 || hasMore) && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
          <button className="btn btn-ghost btn-sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
          <span style={{ lineHeight: '28px', fontSize: 13, color: 'var(--text-secondary)' }}>Page {page}</span>
          <button className="btn btn-ghost btn-sm" disabled={!hasMore} onClick={() => setPage(p => p + 1)}>Next →</button>
        </div>
      )}

      {/* New Project Modal */}
      {showNew && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1000,
                      display: 'flex', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'center', padding: isMobile ? 0 : 16 }}
             onClick={() => setShowNew(false)}>
          <div style={ isMobile
              ? { background: 'var(--bg-surface)', borderRadius: 0, padding: 18, width: '100vw', height: '100vh', maxHeight: '100vh', overflowY: 'auto' }
              : { background: 'var(--bg-surface)', borderRadius: 16, padding: 28, width: '100%', maxWidth: 580, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,.2)' }}
               onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 20 }}>New Project</h3>
            <form onSubmit={handleCreate}>

              {/* Title */}
              <div style={{ marginBottom: 14 }}>
                <label style={lbl}>Title *</label>
                <input className="input" required placeholder="Project title…"
                  value={newForm.title} onChange={e => setNewForm(f => ({ ...f, title: e.target.value }))} />
              </div>

              {/* Type */}
              <div style={{ marginBottom: 14 }}>
                <label style={lbl}>Engagement Type *</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {TYPE_OPTIONS.map(t => (
                    <button key={t} type="button" onClick={() => { setNewForm(f => ({ ...f, type: t, template_id: '' })); setTplPreview(null); }}
                      style={{ flex: 1, padding: '8px 0', borderRadius: 8, border: '1.5px solid',
                               borderColor: newForm.type === t ? '#0B6623' : 'var(--n-300)',
                               background: newForm.type === t ? '#f0fdf4' : 'var(--bg-surface)',
                               color: newForm.type === t ? '#0B6623' : 'var(--text-secondary)',
                               fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              {/* Customer picker */}
              <div style={{ marginBottom: 14 }}>
                <label style={lbl}>Customer / Prospect *</label>
                <CustomerPicker
                  value={newForm.customer_id}
                  onChange={id => setNewForm(f => ({ ...f, customer_id: id }))}
                  products={products}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                <div>
                  <label style={lbl}>Product</label>
                  <select className="input" value={newForm.product_id}
                    onChange={e => {
                      setNewForm(f => ({ ...f, product_id: e.target.value, template_id: '' }));
                      setTplPreview(null);
                    }}>
                    <option value="">Select product…</option>
                    {products.map(p => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={lbl}>Manager</label>
                  <select className="input" value={newForm.manager_id} onChange={e => setNewForm(f => ({ ...f, manager_id: e.target.value }))}>
                    <option value="">— Assign to me —</option>
                    {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={lbl}>Start Date</label>
                  <input type="date" className="input" value={newForm.start_date} onChange={e => handleStartDateChange(e.target.value)} />
                </div>
                <div>
                  <label style={lbl}>Target Date</label>
                  <input type="date" className="input" value={newForm.target_date} onChange={e => setNewForm(f => ({ ...f, target_date: e.target.value }))} />
                </div>
              </div>

              {/* Template selector — filtered by type + product */}
              {(() => {
                const filtered = newForm.product_id
                  ? templates.filter(t =>
                      t.type === newForm.type &&
                      (!t.product_id || String(t.product_id) === String(newForm.product_id))
                    )
                  : [];
                return (
                  <div style={{ marginBottom: 14 }}>
                    <label style={lbl}>
                      Template
                      <span style={{ fontWeight: 400, color: '#94a3b8', marginLeft: 6 }}>
                        {!newForm.product_id
                          ? '— select a product first'
                          : filtered.length === 0
                            ? '— no templates for this type/product yet'
                            : `(${filtered.length} available)`}
                      </span>
                    </label>
                    <select className="input" value={newForm.template_id}
                      onChange={e => handleTplChange(e.target.value)}
                      disabled={filtered.length === 0}>
                      <option value="">— No template —</option>
                      {filtered.map(t => (
                        <option key={t.id} value={t.id}>
                          {t.name}{t.owner_id ? ' (Mine)' : ''}{t.product_name ? ` · ${t.product_name}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })()}

              {/* Milestone preview */}
              {tplPreview && tplPreview.milestones.length > 0 && (
                <div style={{ marginBottom: 16, padding: 14, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#065f46', marginBottom: 8 }}>
                    📋 Plan Preview — {tplPreview.name}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {tplPreview.milestones.map((m, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#0B6623', background: '#fff',
                                       border: '1px solid #bbf7d0', borderRadius: 99, padding: '1px 7px', flexShrink: 0 }}>M{i+1}</span>
                        <span style={{ flex: 1, color: 'var(--text-primary)', fontWeight: 500 }}>{m.title}</span>
                        {m.computed_due ? (
                          <span style={{ fontSize: 11, color: '#065f46', fontFamily: 'monospace', flexShrink: 0 }}>
                            {new Date(m.computed_due).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </span>
                        ) : (
                          <span style={{ fontSize: 11, color: '#94a3b8', flexShrink: 0 }}>No due date</span>
                        )}
                        {m.tasks && m.tasks.length > 0 && (
                          <span style={{ fontSize: 11, color: '#94a3b8', flexShrink: 0 }}>{m.tasks.length} tasks</span>
                        )}
                      </div>
                    ))}
                  </div>
                  {!newForm.start_date && (
                    <div style={{ fontSize: 11, color: '#92400e', marginTop: 8 }}>
                      ⚠ Set a Start Date to see exact milestone dates
                    </div>
                  )}
                </div>
              )}

              <div style={{ marginBottom: 20 }}>
                <label style={lbl}>Notes</label>
                <textarea className="input" rows={2} style={{ resize: 'none' }} value={newForm.notes}
                  onChange={e => setNewForm(f => ({ ...f, notes: e.target.value }))} />
              </div>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-ghost" onClick={() => setShowNew(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={creating}>
                  {creating ? 'Creating…' : 'Create Project'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
